import { useEffect, useState } from 'react';

import { Box, Typography } from '@mui/material';

import { Maybe } from '@/types';
import { getBlogListByBrand } from '@/services/getBlogListByBrand';
import { BlogDetailsPage, BlogDetailsPageCollection, BlogList as TBlogList } from '@/types/generated';
import { logWarning } from '@/utils/miscUtils';
import { useAppContext } from '@/context/AppContextProvider.ctx';
import { getNumberOfBlogs } from '@/utils';

import BlogListPageDefault from './BlogListPageDefault/BlogListPageDefault';
import BlogsHalfScreen from './BlogsHalfScreen/BlogsHalfScreen';
import BlogsListHero from './BlogsListHero/BlogsListHero';
import { ICardDesignType } from './BlogListItem';
import { blogListTitlerContainerStyles } from './BlogListPageDefault/BlogListPageDefault.styles';

export type BlogListResponse = {
	blogDetailsPageCollection: BlogDetailsPageCollection;
	total: number;
	skip: number;
	limit: number;
};

export interface IBlogList extends TBlogList {
	/**
	 * Used while fetching the blog list.
	 *
	 * @type {Boolean}
	 * @memberof IBlogList
	 */
	preview?: boolean;
	cardDesignType?: ICardDesignType;
	startIndexBlogList: number;
	setStartIndexBlogList: (startIndex: number) => void;
	categoryName?: string;
	subCategoryName?: string;
	tags?: Array<string>;
}

export const getPaginationPageIndex = (pageNumber: number, countPerPage: number) => {
	return (pageNumber - 1) * countPerPage;
};

const BlogListTitle = ({ title }: { title?: Maybe<string> }) => {
	return (
		<>
			{title && (
				<Box sx={blogListTitlerContainerStyles}>
					<Typography component="h3" variant="header2" color="text.primary" data-test-id="blog_list_title">
						{title}
					</Typography>
				</Box>
			)}
		</>
	);
};

export default function BlogList({
	title,
	displayType,
	cardDesignType,
	brand,
	buttonLabel,
	loadMoreButtonLabel,
	preview = false,
	heroBlogsListCollection,
	countAcrossDesktop,
	countAcrossMobile,
	hasPagination = false,
	featuredBlogsListCollection,
	startIndexBlogList = 0,
	setStartIndexBlogList,
	categoryName,
	subCategoryName,
	tagsFilter,
	tags,
}: Readonly<IBlogList>) {
	const isFeatured = displayType === 'Featured';
	const isTagType = displayType === 'Tag';

	const numberOfBlogs = getNumberOfBlogs(displayType as string);
	const [state, setState] = useState<{
		totalBlogs: number;
		displayShowMoreButton: boolean;
		blogsList: Array<BlogDetailsPage>;
	}>({
		totalBlogs: 0,
		blogsList: [],
		displayShowMoreButton: false,
	});

	const { environment, config } = useAppContext();
	const contentfulGraphqlURL =
		environment === 'prod'
			? config.services.prod.CONTENTFUL_GRAPHQL_URL
			: config.services.nonprod.CONTENTFUL_GRAPHQL_URL;

	// brand name comes from blog list content itself or take it from context
	const brandNameForQuery = brand?.title ?? config.name;

	const getStateReset = () => ({
		...state,
		blogsList: [],
		displayShowMoreButton: false,
		totalBlogs: 0,
	});
	let filterByTags: Array<string> | undefined;
	if (isTagType) {
		filterByTags = tagsFilter?.length ? (tagsFilter as Array<string>) : tags;
	} else {
		filterByTags = undefined;
	}
	useEffect(() => {
		const featuredBlogsList = featuredBlogsListCollection?.items as BlogDetailsPage[];
		if (isFeatured) {
			setState({
				...state,
				blogsList: featuredBlogsList,
				displayShowMoreButton: false,
				totalBlogs: featuredBlogsListCollection?.items.length ?? 2,
			});
			return;
		}
		getBlogListByBrand(
			numberOfBlogs,
			startIndexBlogList,
			brandNameForQuery,
			contentfulGraphqlURL,
			preview,
			categoryName,
			subCategoryName,
			filterByTags
		)
			.then((blogsListResponse: BlogListResponse | undefined) => {
				if (blogsListResponse) {
					const blogDetailsPageItems = blogsListResponse.blogDetailsPageCollection
						?.items as Array<BlogDetailsPage>;
					const totalBlogs = blogsListResponse.blogDetailsPageCollection?.total ?? 0;
					const blogDetailsList =
						startIndexBlogList === 0 ? blogDetailsPageItems : state.blogsList.concat(blogDetailsPageItems);
					setState({
						...state,
						blogsList: blogDetailsList,
						displayShowMoreButton: displayType === 'Blog List Page' && blogDetailsList.length < totalBlogs,
						totalBlogs,
					});
				}
				return blogsListResponse;
			})
			.catch((error) => {
				// Handle any errors that occurred during the API call.
				// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
				logWarning(`Error fetching blogs list by brand error: ${error}`);
				setState(getStateReset());
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [startIndexBlogList, categoryName, subCategoryName]);

	const handleShowMoreClick = () => {
		setStartIndexBlogList(startIndexBlogList + numberOfBlogs);
		setState({
			...state,
			displayShowMoreButton: false,
		});
	};

	const handlePaginationChangeEvent = (e: React.ChangeEvent<unknown>, page: number) => {
		const indexByPage = getPaginationPageIndex(page, numberOfBlogs);
		window.scrollTo({ top: 0 });
		setState(getStateReset());

		getBlogListByBrand(
			numberOfBlogs,
			indexByPage,
			brandNameForQuery,
			contentfulGraphqlURL,
			preview,
			categoryName,
			subCategoryName
		)
			.then((blogsListResponse: BlogListResponse | undefined) => {
				if (blogsListResponse) {
					setState({
						...state,
						blogsList: blogsListResponse.blogDetailsPageCollection?.items as Array<BlogDetailsPage>,
					});
					window.scrollTo({ top: 0, behavior: 'smooth' });
				}
				return blogsListResponse;
			})
			.catch((error) => {
				// Handle any errors that occurred during the API call.
				// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
				logWarning(`Error fetching blogs list by brand error: ${error}`);
				setState(getStateReset());
			});

		setStartIndexBlogList(0);
	};

	switch (displayType) {
		case 'Half Screen Left':
		case 'Half Screen Right':
			return (
				<BlogsHalfScreen
					displayType={displayType}
					blogsList={state.blogsList}
					buttonLabel={buttonLabel as string}
					loadMoreButtonLabel={loadMoreButtonLabel as string}
				/>
			);
		case 'Hero':
			return (
				<BlogsListHero
					blogsList={heroBlogsListCollection?.items as Array<BlogDetailsPage>}
					buttonLabel={buttonLabel}
				/>
			);
		default:
			return (
				<Box sx={{ width: '100%' }}>
					<BlogListTitle title={title} />
					<BlogListPageDefault
						totalBlogs={state.totalBlogs}
						blogsList={state.blogsList}
						buttonLabel={buttonLabel as string}
						loadMoreButtonLabel={loadMoreButtonLabel as string}
						displayShowMoreButton={state.displayShowMoreButton}
						handleShowMoreClick={handleShowMoreClick}
						handlePaginationChangeEvent={handlePaginationChangeEvent}
						cardDesignType={cardDesignType}
						countAcrossDesktop={countAcrossDesktop as number}
						countAcrossMobile={countAcrossMobile as number}
						hasPagination={hasPagination}
						limit={12}
						displayType={displayType}
					/>
				</Box>
			);
	}
}
